import { Icon } from './icons'
import { Bar, DataState, MCAvatar, Sparkline, StatusDot } from './shared'
import { fmtMB, fmtUptime } from './format'
import type { ApiError, DashLayout, LifecycleAction, Role, Server, StatusDotKind, VpsStats } from './types'

function statusKindOf(server: Server): StatusDotKind {
  if (server.status === 'online') return 'online'
  if (server.status === 'hibernating' || server.status === 'starting' || server.status === 'stopping' || server.status === 'restarting') return 'warn'
  return 'offline'
}

function statusLabelOf(server: Server): string {
  if (server.status === 'online') return 'Online'
  if (server.status === 'hibernating') return 'Hibernating'
  if (server.status === 'starting') return 'Starting'
  if (server.status === 'stopping') return 'Stopping'
  if (server.status === 'restarting') return 'Restarting'
  return 'Offline'
}

function tpsLabel(server: Server): string {
  return server.tps !== null ? server.tps.toFixed(1) : '--'
}

function uptimeLabel(server: Server): string {
  if (server.status === 'online') return fmtUptime(server.uptimeSec)
  if (server.status === 'hibernating') return 'warm on join'
  return '--'
}

function ramUsagePct(server: Server): number {
  if (server.ramMaxMb <= 0) return 0
  return (server.ramMb / server.ramMaxMb) * 100
}

export function VpsStrip({ vps }: { vps: VpsStats }) {
  return (
    <div className="vps-strip">
      <div className="vps-tile">
        <div className="vps-label">CPU</div>
        <div className="vps-val">{vps.cpu}%</div>
        <Sparkline data={vps.cpuSpark} color="var(--accent-400)" height={22} />
      </div>
      <div className="vps-tile">
        <div className="vps-label">RAM</div>
        <div className="vps-val">{vps.ram}%</div>
        <div className="vps-sub">{vps.ramUsedGb} / {vps.ramTotalGb} GB</div>
        <Sparkline data={vps.ramSpark} color="var(--emerald-400)" height={22} />
      </div>
      <div className="vps-tile">
        <div className="vps-label">Disk</div>
        <div className="vps-val">{vps.disk}%</div>
        <div className="vps-sub">{vps.diskUsedGb} / {vps.diskTotalGb} GB</div>
        <Sparkline data={vps.diskSpark} color="var(--amber-400)" height={22} />
      </div>
      <div className="vps-tile">
        <div className="vps-label">Load</div>
        <div className="vps-val" style={{ fontSize: 18 }}>{vps.load.join(' / ')}</div>
        <div className="vps-sub">1m / 5m / 15m</div>
      </div>
    </div>
  )
}

interface CardProps {
  server: Server
  role: Role
  onOpen: () => void
  onAction: (action: LifecycleAction, server: Server) => void
}

export function ServerCard({ server, role, onOpen, onAction }: CardProps) {
  const canLifecycle = role === 'owner' || role === 'admin'
  const statusKind = statusKindOf(server)
  const statusLabel = statusLabelOf(server)
  const busy = Boolean(server.operation)

  return (
    <div className="server-card" onClick={onOpen}>
      <div className="sc-head">
        <StatusDot kind={statusKind} />
        <h3>{server.name}</h3>
        <span className="chip">{server.version}</span>
        {server.msh && <span className="chip">msh</span>}
        {server.operation && <span className="chip accent">{server.operation.label}</span>}
        <span className="ml-auto dim text-xs">{server.oneLiner}</span>
      </div>
      <div className="sc-body">
        <div className="sc-stat">
          <div className="label">Status</div>
          <div className="val" style={{ color: server.status === 'online' ? 'var(--emerald-400)' : statusKind === 'warn' ? 'var(--amber-400)' : 'var(--text-dim)' }}>
            {statusLabel}
          </div>
        </div>
        <div className="sc-stat">
          <div className="label">Players</div>
          <div className="val">{server.players.length} / {server.maxPlayers}</div>
        </div>
        <div className="sc-stat">
          <div className="label">TPS</div>
          <div className="val">{tpsLabel(server)}</div>
        </div>
        <div className="sc-stat">
          <div className="label">Uptime</div>
          <div className="val">{uptimeLabel(server)}</div>
        </div>
        <div className="sc-stat">
          <div className="label">CPU</div>
          <div className="val">{server.cpu}%</div>
        </div>
        <div className="sc-stat">
          <div className="label">RAM</div>
          <div className="val">{fmtMB(server.ramMb)}{server.ramMaxMb > 0 && <span className="dim text-xs"> / {fmtMB(server.ramMaxMb)}</span>}</div>
        </div>
      </div>
      {canLifecycle && (
        <div className="sc-actions" onClick={event => event.stopPropagation()}>
          {server.status === 'online' ? (
            <>
              <button className="btn danger" onClick={() => onAction('stop', server)} disabled={busy}>
                <Icon name="square" size={12} /> Stop
              </button>
              <button className="btn" onClick={() => onAction('restart', server)} disabled={busy}>
                <Icon name="rotate-ccw" size={12} /> Restart
              </button>
            </>
          ) : (
            <button className="btn primary" onClick={() => onAction('start', server)} disabled={busy || server.status === 'starting' || server.status === 'stopping' || server.status === 'restarting'}>
              <Icon name="play" size={12} /> Start
            </button>
          )}
          <button className="btn ghost ml-auto" onClick={event => { event.stopPropagation(); onOpen() }}>
            Open <Icon name="chevron-right" size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

export function ServerRow({ server, role, onOpen, onAction }: CardProps) {
  const canLifecycle = role === 'owner' || role === 'admin'
  const statusKind = statusKindOf(server)

  return (
    <div className="server-row" onClick={onOpen}>
      <StatusDot kind={statusKind} />
      <div>
        <div className="name">{server.name}</div>
        <div className="dim text-xs">{server.oneLiner}</div>
      </div>
      <div className="stat"><span className="lbl">Players</span>{server.players.length}/{server.maxPlayers}</div>
      <div className="stat"><span className="lbl">TPS</span>{tpsLabel(server)}</div>
      <div className="stat"><span className="lbl">Uptime</span>{uptimeLabel(server)}</div>
      {canLifecycle && (
        <div className="flex gap-2" onClick={event => event.stopPropagation()}>
          {server.status === 'online' ? (
            <button className="btn sm" onClick={() => onAction('restart', server)} disabled={Boolean(server.operation)}><Icon name="rotate-ccw" size={11} /></button>
          ) : (
            <button className="btn sm primary" onClick={() => onAction('start', server)} disabled={Boolean(server.operation)}><Icon name="play" size={11} /></button>
          )}
        </div>
      )}
    </div>
  )
}

export function ServerHero({ server, role, onOpen, onAction }: CardProps) {
  const canLifecycle = role === 'owner' || role === 'admin'
  const statusKind = statusKindOf(server)
  const ramPct = ramUsagePct(server)

  return (
    <div className="server-hero">
      <div className="side">
        <h2>
          <StatusDot kind={statusKind} />
          {server.name}
          <span className="chip" style={{ marginLeft: 'auto' }}>{server.version}</span>
          {server.operation && <span className="chip accent">{server.operation.label}</span>}
        </h2>
        <div className="big-stats">
          <div className="bs"><span className="lbl">Status</span><span className="val" style={{ color: server.status === 'online' ? 'var(--emerald-400)' : statusKind === 'warn' ? 'var(--amber-400)' : 'var(--text-dim)' }}>{statusLabelOf(server)}</span></div>
          <div className="bs"><span className="lbl">Players</span><span className="val">{server.players.length}/{server.maxPlayers}</span></div>
          <div className="bs"><span className="lbl">TPS</span><span className="val">{tpsLabel(server)}</span></div>
        </div>
        <div className="flex gap-2">
          {canLifecycle && server.status === 'online' && (
            <>
              <button className="btn danger" onClick={() => onAction('stop', server)} disabled={Boolean(server.operation)}><Icon name="square" size={12} /> Stop</button>
              <button className="btn" onClick={() => onAction('restart', server)} disabled={Boolean(server.operation)}><Icon name="rotate-ccw" size={12} /> Restart</button>
            </>
          )}
          {canLifecycle && server.status !== 'online' && (
            <button className="btn primary" onClick={() => onAction('start', server)} disabled={Boolean(server.operation)}><Icon name="play" size={12} /> Start</button>
          )}
          <button className="btn ghost ml-auto" onClick={onOpen}>Open console <Icon name="chevron-right" size={12} /></button>
        </div>
      </div>
      <div className="side" style={{ background: 'var(--bg)' }}>
        <div className="text-xs dim" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Online players</div>
        {server.players.length ? server.players.map(player => (
          <div key={player} className="player-row">
            <MCAvatar name={player} size="sm" />
            <span className="pname">{player}</span>
            <span className="pd">- playing</span>
          </div>
        )) : <div className="dim text-sm">Nobody is online right now.</div>}
        <div className="text-xs dim mt-3" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Resource usage</div>
        <div className="flex gap-3 mt-2">
          <div style={{ flex: 1 }}>
            <div className="text-xs dim mb-1">CPU {server.cpu}%</div>
            <Bar pct={server.cpu} color="var(--accent-400)" />
          </div>
          <div style={{ flex: 1 }}>
            <div className="text-xs dim mb-1">RAM {Math.round(ramPct)}%</div>
            <Bar pct={ramPct} color="var(--emerald-400)" />
          </div>
        </div>
      </div>
    </div>
  )
}

interface DashboardProps {
  servers: Server[]
  vps: VpsStats
  role: Role
  setRoute: (route: string) => void
  onAction: (action: LifecycleAction, server: Server) => void
  layout: DashLayout
  serversError: ApiError | null
  serversLoading: boolean
  onRetry: () => void
}

export function DashboardPage({ servers, vps, role, setRoute, onAction, layout, serversError, serversLoading, onRetry }: DashboardProps) {
  return (
    <>
      <div className="page-header" data-screen-label="Dashboard">
        <h1>Dashboard</h1>
        <span className="subtitle">- all systems nominal - {new Date().toLocaleString('en', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
      </div>

      <VpsStrip vps={vps} />

      <DataState
        loading={serversLoading && servers.length === 0}
        error={serversError}
        empty={!serversLoading && !serversError && servers.length === 0}
        emptyLabel="No servers configured. Add entries to /etc/mcpanel/servers.yaml."
        onRetry={onRetry}
      >
        {layout === 'cards' && (
          <div className="server-grid">
            {servers.map(server => (
              <ServerCard
                key={server.id}
                server={server}
                role={role}
                onOpen={() => setRoute(`srv:${server.id}:overview`)}
                onAction={onAction}
              />
            ))}
          </div>
        )}
        {layout === 'dense' && (
          <div className="server-list">
            {servers.map(server => (
              <ServerRow
                key={server.id}
                server={server}
                role={role}
                onOpen={() => setRoute(`srv:${server.id}:overview`)}
                onAction={onAction}
              />
            ))}
          </div>
        )}
        {layout === 'hero' && (
          <div className="flex-col gap-4">
            {servers.map(server => (
              <ServerHero
                key={server.id}
                server={server}
                role={role}
                onOpen={() => setRoute(`srv:${server.id}:console`)}
                onAction={onAction}
              />
            ))}
          </div>
        )}
      </DataState>
    </>
  )
}
